import { dashboardTabs } from 'data';
import { IApplyDashboardRowTotals, IApplyDashboardTabs, IApplyDashboardTabStatusMap } from 'types';
import { GroupedCustomTabs, UserRole } from './types';
import { isPartnerRole } from '@/utilities/roles/permissions';
import { NamedRoutes } from '@/types';
import { requestDashboardRows } from '@/store/offers-dashboard/actions';

/**
 * Returns the correct dashboard tabs for a given user role.
 * Logs an error on missing tabs.
 * @param userRole
 * @returns
 */
export function getDashboardTabs(userRole: UserRole): {
  tabs: IApplyDashboardTabs;
  map: IApplyDashboardTabStatusMap;
} {
  const { tabs, map } = dashboardTabs[userRole] ?? {};

  if (!tabs) {
    console.error(`Dashboard tabs are not assigned to your role: ${userRole}`);
    return { tabs, map }; // Return empty defaults
  }

  return { tabs, map };
}

/**
 * Helper function that handles refreshing the rows of the table based upon the current tab.
 * Used by custom react hooks in ./hooks.ts
 * @param dispatch
 * @param activeTab
 */
export function refreshRows(dispatch) {
  dispatch(requestDashboardRows());
}

export function assignActiveTab(customTabs: GroupedCustomTabs, activeTab: string): { customTabs: GroupedCustomTabs } {
  for (const group of Object.keys(customTabs)) {
    for (const tab of customTabs[group]) {
      if (activeTab === tab.id) {
        tab.active = true;
      }
    }
  }
  return { customTabs };
}

/**
 * Helper method that generates GroupedCustomTabs from the dashboard tabs located in the ApplyUniConfig.
 * @param activeTab {string}
 * @param applyConfig {IApplyUniConfig}
 * @param totals {IApplyDashboardRowTotals}
 * @returns GroupedCustomTabs
 */
export function generateTabs(
  activeTab: string,
  totals: IApplyDashboardRowTotals,
  userRole: UserRole,
): { customTabs: GroupedCustomTabs } {
  const { tabs, map } = getDashboardTabs(userRole);
  const customTabs: GroupedCustomTabs = Object.keys(tabs).reduce((prev, group) => {
    // Tabs are grouped into sections (groups) for easier rendering in the UI.
    const groupTabs = tabs?.[group]?.map(tab => {
      let total = 0;
      const applicationStatuses = map?.[tab.id]?.applicationStatuses ?? [];
      const offerStatuses = map?.[tab.id]?.offerStatuses ?? [];

      const statuses = [...applicationStatuses, ...offerStatuses];

      for (const status of applicationStatuses) {
        const totalForStatus = totals?.applicationTotals?.find(totalRow => totalRow.status === status)?.total;
        total += totalForStatus ? Number.parseInt(totalForStatus) : 0;
      }

      for (const status of offerStatuses) {
        const totalForStatus = totals?.offerTotals?.find(totalRow => totalRow.status === status)?.total;
        total += totalForStatus ? Number.parseInt(totalForStatus) : 0;
      }

      return {
        ...tab,
        id: tab.id,
        label: tab.label,
        statuses,
        count: String(total ?? 0),
        active: activeTab === tab.id,
        color: tab.color,
      };
    });

    return { ...prev, [group]: groupTabs };
  }, {});

  return {
    customTabs,
  };
}

export function getPartnerWorkflowLogic(
  userRole: UserRole,
  partnerFullAccess: boolean,
  partnerAdmissionAccess: boolean,
  location: { pathname: string },
): {
  shouldShowDropdown: boolean;
  shouldAutoNavigate: boolean;
  targetPath: string | null;
} {
  if (!isPartnerRole(userRole)) {
    return {
      shouldShowDropdown: true,
      shouldAutoNavigate: false,
      targetPath: null,
    };
  }

  if (partnerFullAccess) {
    return {
      shouldShowDropdown: true,
      shouldAutoNavigate: false,
      targetPath: null,
    };
  }

  if (partnerAdmissionAccess) {
    return {
      shouldShowDropdown: true,
      shouldAutoNavigate: location.pathname !== NamedRoutes.ROOT && location.pathname !== '/',
      targetPath: NamedRoutes.ADMISSIONS_WORKFLOW,
    };
  }

  return {
    shouldShowDropdown: false,
    shouldAutoNavigate: location.pathname !== NamedRoutes.ROOT && location.pathname !== '/',
    targetPath: NamedRoutes.ROOT,
  };
}
